import {
  differenceWith,
  flow,
  groupBy,
  isEmpty,
  isEqual,
  isNil,
  mapValues,
  sum,
  sumBy,
  unionBy,
} from 'lodash-es'
import { DateTime } from 'luxon'

import {
  ConfigurationState,
  getProduct,
  isProductDocument,
  ProductDocument,
  Scheme,
} from '../state/configuration'
import { getPriceBreakdown } from './quote'
import {
  AddCoverDisclosureChange,
  AdditionalPriceOption,
  CoverOption,
  CoverType,
  PolicyChangeType,
  PriceOption,
  Quote,
  RemoveCoverDisclosureChange,
} from './types'

export const isCoverActive = (priceOption: PriceOption) => Boolean(priceOption.active)

export const getPremium = (priceBreakdown: PriceOption[]) =>
  sum(priceBreakdown.map((it) => it.premium))

export const getTotalPremium = (priceBreakdown: PriceOption[]) =>
  sum(priceBreakdown.map((it) => it.totalPremium))

export const getTaxAmount = (priceBreakdown: PriceOption[]) =>
  sum(priceBreakdown.map((it) => it.taxAmount))

export const getAdditionalItemsTotals = flow(
  // Group by tag, sum premium, take absolute values
  (items: AdditionalPriceOption[]) => groupBy(items, (it) => it.tag),
  (grouped) => mapValues(grouped, (items) => Math.abs(sumBy(items, (it) => it.premium))),
)

export const getCoversByCoverType = (
  priceBreakdown: PriceOption[],
  coverType: CoverType,
) => priceBreakdown.filter((it) => it.coverType === coverType)

export const hasCoverGroup =
  (coverGroup: string | undefined) => (priceOption: PriceOption) =>
    priceOption.coverGroup === coverGroup

export const getCovers = (priceBreakdown: PriceOption[], coverGroup: string) =>
  priceBreakdown.filter((it) => it.coverGroup === coverGroup)

export interface GeneratedProductDocument extends ProductDocument {
  url: string
}

export const getProductDocuments =
  (scheme: Scheme, quote: Quote) =>
  (state: ConfigurationState): GeneratedProductDocument[] => {
    const product = getProduct(scheme.schemeCode)(state)
    if (!product) return []

    const generateDocumentUrl = (parts: {
      document: ProductDocument
      insurer: string
      wordingVersion: string
    }) => `/documents/${parts.wordingVersion}/${parts.insurer}.${parts.document.id}.html`

    const priceBreakdown = getPriceBreakdown(quote)

    return product.documents
      .filter(isProductDocument)
      .map((doc) => ({
        doc,
        priceOption: priceBreakdown
          ?.filter(hasCoverGroup(doc.coverCode))
          .find(isCoverActive),
      }))
      .filter(({ doc, priceOption }) => isNil(doc.coverCode) || !isNil(priceOption))
      .map(({ doc, priceOption }) => ({
        ...doc,
        url: generateDocumentUrl({
          document: doc,
          insurer: priceOption?.insurer ?? quote.mainInsurer,
          wordingVersion: quote.wordingVersion,
        }),
      }))
  }

export const coverDifferencesToDisclosureChange = (
  assetCode: string,
  updatedCoverOptions: CoverOption[],
  originalCoverOptions: CoverOption[],
): Array<AddCoverDisclosureChange | RemoveCoverDisclosureChange> => {
  const differences = differenceWith(
    updatedCoverOptions.map((it) => ({ ...it, active: Boolean(it.active) })),
    originalCoverOptions,
    isEqual,
  )

  return differences.map(({ active, coverGroup }) => ({
    changeType: active ? PolicyChangeType.ADD_COVER : PolicyChangeType.REMOVE_COVER,
    coverOption: coverGroup,
  }))
}

export const areCoversEqual = (left: PriceOption[], right: PriceOption[]) =>
  isEmpty(differenceWith(left, right, isEqual))

export const mergePriceBreakdowns = (
  updates: PriceOption[],
  original: PriceOption[],
): PriceOption[] => unionBy(updates, original, (cover) => cover.coverGroup)

export const arePremiumsEqual = (previous: Quote, current: Quote) =>
  areCoversEqual(getPriceBreakdown(previous) ?? [], getPriceBreakdown(current) ?? [])

export const inForce = (price: PriceOption | undefined): boolean =>
  Boolean(
    price?.active &&
      (isNil(price.endAt) || DateTime.local() < DateTime.fromISO(price.endAt)),
  )

export const isPriceOptionRemoved = (
  previous: PriceOption | undefined,
  current: PriceOption | undefined,
): boolean => {
  if (inForce(previous)) {
    return !inForce(current)
  } else {
    return false
  }
}
